import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Stat } from "../_ui";
import { ScaledPreview } from "./theme_picker/ThemeTile";

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_W = 1440;
const PREVIEW_H = 440;
const MAX_SCALE = 0.55;

function PalettePill({ palette, font }) {
  if (!palette) return null;
  return (
    <div className="flex items-center gap-2 text-[11px] mono text-zinc-400" data-testid="static-overview-palette">
      <span className="inline-flex h-4 w-14 rounded-sm overflow-hidden border border-zinc-700">
        {["hero", "primary", "accent", "bg"].map((k) => <span key={k} className="flex-1" style={{ background: palette[k] }} />)}
      </span>
      <span>{palette.label}</span>
      {font && <span className="text-zinc-600">· {font.label}</span>}
    </div>
  );
}

/** Live scaled iframe of the static home page + refresh (cache-bust) + open in new tab. */
export function StaticHomePreview({ site, status }) {
  const [bust, setBust] = useState(() => Date.now());
  const [cw, setCw] = useState(640);
  const ref = React.useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const fit = () => setCw(el.clientWidth || 640);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = Math.max(0.2, Math.min(MAX_SCALE, (cw - 24) / PREVIEW_W));
  const src = `${API}/api/render/sites/${site.slug}?_=${bust}`;
  return (
    <div className="lg:col-span-3 border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-hidden" data-testid="static-home-preview">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-800">
        <div className="min-w-0">
          <div className="label-tiny">Live home page</div>
          <PalettePill palette={status?.palette} font={status?.font} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="static-preview-refresh" onClick={() => setBust(Date.now())}
                  className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-2.5 py-1.5 rounded-sm text-xs font-semibold">
            <RefreshCw size={12} /> Refresh
          </button>
          <a data-testid="static-preview-open" href={`${API}/api/render/sites/${site.slug}`} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-200 px-2.5 py-1.5 rounded-sm text-xs font-semibold">
            Open <ExternalLink size={12} />
          </a>
        </div>
      </div>
      <div ref={ref} className="w-full flex justify-center bg-zinc-950 p-3">
        <div style={{ width: PREVIEW_W * scale }} className="border border-zinc-800 rounded-sm overflow-hidden shadow-xl">
          <ScaledPreview src={src} title={`${site.name} — home`} w={PREVIEW_W * scale} h={PREVIEW_H} scale={scale} iframeKey={bust} />
        </div>
      </div>
    </div>
  );
}

/** Overview tab body for static (lead-gen) sites — replaces the news content pipeline. */
export default function StaticOverview({ site }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get(`/sites/${site.id}/static`).then((r) => setStatus(r.data)).catch(() => {});
  }, [site.id, site.updated_at]);
  const copyState = status?.copy_generated_at ? "AI reworded" : status?.copy_error ? "Reword failed" : "Default copy";
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="static-overview">
      <Stat label="Lead form" value={site.has_form_embed ? "Set" : "Missing"} sub={site.has_form_embed ? "snippet rendered in #offer" : "paste it in Settings → Lead form snippet"} />
      <Stat label="Marketing copy" value={copyState} sub={status?.copy_generated_at ? new Date(status.copy_generated_at).toLocaleString() : status?.copy_error ? status.copy_error.slice(0, 60) : "reword pending or not run"} />
      <Stat label="Status" value={site.status} sub={`AI spend today $${(site.ai_spend_today_usd || 0).toFixed(3)}`} />
      <StaticHomePreview site={site} status={status} />
      <div className="lg:col-span-3 text-[11px] text-zinc-500 mono flex items-center gap-3 flex-wrap" data-testid="static-overview-links">
        <span>Change palette, fonts, copy or the form snippet in</span>
        <Link to={`/admin/sites/${site.id}?tab=settings`} className="text-amber-400 underline">Settings</Link>
        {(status?.pages || []).filter((p) => p.slug).map((p) => (
          <a key={p.slug} href={`${API}${p.preview}`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white">{p.path}</a>
        ))}
      </div>
    </div>
  );
}
